<script lang="ts">
  import { _ } from 'svelte-i18n';
  
  interface Props {
    url: string;
    title: string;
    description: string;
    showLabels?: boolean;
  }
  
  let { url, title, description, showLabels = false }: Props = $props();
  
  function shareOnTwitter() {
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`;
    window.open(twitterUrl, '_blank', 'width=550,height=420');
    
    trackShare('Twitter');
  }
  
  function shareOnLinkedIn() {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedInUrl, '_blank', 'width=550,height=420');
    
    trackShare('LinkedIn');
  }
  
  function shareOnFacebook() {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'width=550,height=420');
    
    trackShare('Facebook');
  }
  
  function copyToClipboard() {
    navigator.clipboard.writeText(url).then(() => {
      // Show success feedback
      const button = document.querySelector('.share-button--copy');
      if (button) {
        button.textContent = showLabels ? $_('share.copied') : '✓';
        setTimeout(() => {
          button.textContent = showLabels ? $_('share.copy') : '🔗';
        }, 2000);
      }
      
      trackShare('Copy Link');
    }).catch(() => {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      trackShare('Copy Link');
    });
  }
  
  function trackShare(platform: string) {
    if (typeof window !== 'undefined' && window.plausible) {
      window.plausible('Content Share', {
        props: { platform, url }
      });
    }
  }
</script>

<div class="social-share">
  {#if showLabels}
    <p class="social-share__label">{$_('share.title')}</p>
  {/if}
  
  <div class="social-share__buttons">
    <button
      class="share-button share-button--twitter"
      onclick={shareOnTwitter}
      title={$_('share.twitter')}
      aria-label={$_('share.twitter')}
    >
      <svg class="share-button__icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
      {#if showLabels}
        <span class="share-button__label">{$_('share.twitter')}</span>
      {/if}
    </button>
    
    <button
      class="share-button share-button--linkedin"
      onclick={shareOnLinkedIn}
      title={$_('share.linkedin')}
      aria-label={$_('share.linkedin')}
    >
      <svg class="share-button__icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
      {#if showLabels}
        <span class="share-button__label">{$_('share.linkedin')}</span>
      {/if}
    </button>
    
    <button
      class="share-button share-button--facebook"
      onclick={shareOnFacebook}
      title={$_('share.facebook')}
      aria-label={$_('share.facebook')}
    >
      <svg class="share-button__icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
      {#if showLabels}
        <span class="share-button__label">{$_('share.facebook')}</span>
      {/if}
    </button>
    
    <button
      class="share-button share-button--copy"
      onclick={copyToClipboard}
      title={$_('share.copy')}
      aria-label={$_('share.copy')}
    >
      🔗
      {#if showLabels}
        <span class="share-button__label">{$_('share.copy')}</span>
      {/if}
    </button>
  </div>
</div>

<style>
  .social-share {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }
  
  .social-share__label {
    font-size: var(--font-size-sm);
    color: var(--text-secondary);
    margin: 0;
  }
  
  .social-share__buttons {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  
  .share-button {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    padding: var(--space-2) var(--space-3);
    border: 1px solid var(--border-light);
    border-radius: var(--radius-md);
    background: var(--bg-primary);
    color: var(--text-secondary);
    font-size: var(--font-size-sm);
    cursor: pointer;
    transition: var(--transition-all);
    text-decoration: none;
  }
  
  .share-button:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  .share-button__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
  
  .share-button__label {
    white-space: nowrap;
  }
  
  .share-button--twitter:hover {
    background: #1da1f2;
    color: white;
    border-color: #1da1f2;
  }
  
  .share-button--linkedin:hover {
    background: #0077b5;
    color: white;
    border-color: #0077b5;
  }
  
  .share-button--facebook:hover {
    background: #1877f2;
    color: white;
    border-color: #1877f2;
  }
  
  .share-button--copy:hover {
    background: var(--brand-primary);
    color: white;
    border-color: var(--brand-primary);
  }
  
  @media (max-width: 768px) {
    .social-share__buttons {
      justify-content: center;
    }
    
    .share-button {
      padding: var(--space-2);
      min-width: 44px;
      justify-content: center;
    }
    
    .share-button__label {
      display: none;
    }
  }
</style>
